/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axiosInterceptors';
import { TProductDetails } from 'utils/types';
import ReactSelect from 'components/base/ReactSelect';
import useDebounce from 'hooks/useDebounce';
import brandLogo from 'assets/img/icons/kandj.png';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faPlusCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import CustomModal, { ModalHandle } from 'components/modals/CustomModal';
import AddProductComponentForm, {
  AddProductComponentFormHandle,
  AddProductComponentFormProps
} from './AddProductComponentForm';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import EditProductComponent from './EditProductComponent';

type ProductOption = {
  label: string;
  value: number;
};

type TProductComponent = {
  description?: string;
  imageFileIds?: string[];
  name?: string | null;
  productBrandId: number;
  productBrandName?: string;
  productCategoryId?: number;
  productCategoryName?: string;
  productId: number;
  quantityPerSet?: number;
  unitOfMeasure?: string;
};

const ProductSetList = () => {
  const [products, setProducts] = useState<TProductDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productComponents, setProductComponents] = useState<
    TProductComponent[]
  >([]);
  const [imageUrls, setImageUrls] = useState<
    Record<number, string | undefined>
  >({});
  const [input, setInput] = useState('');
  const debouncedInput = useDebounce(input, 500);
  const [currentSetId, setCurrentSetId] = useState(0);
  const addModalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const deleteModalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const editModalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addComponentFormRef = useRef<AddProductComponentFormHandle>(null);
  const editComponentFormRef = useRef<AddProductComponentFormHandle>(null);
  const [currentProduct, setCurrentProduct] =
    useState<AddProductComponentFormProps>({
      productId: 0,
      quantity: 0,
      unitOfMeasure: 'Piece'
    });

  const fetchData = async (search: string = '') => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/products?page=1&pageSize=10&search=${search}`
      );
      setProducts(res.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductSetComponents = async (id: number) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/v1/productSets/${id}/components`
      );
      const components = res.data;

      await Promise.all(
        components.map(async (product: TProductDetails) => {
          const imageUrl = await fetchProductImage(product);
          setImageUrls(prev => ({
            ...prev,
            [product.productId]: imageUrl
          }));
        })
      );

      setProductComponents(components);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductImage = async (
    product: TProductDetails
  ): Promise<string | undefined> => {
    try {
      if (product.imageFileIds?.length) {
        const googleFileId = product.imageFileIds[0];
        const res = await axiosInstance.get(
          `/api/v1/products/images/${googleFileId}`,
          { responseType: 'blob' }
        );
        return URL.createObjectURL(res.data);
      }
      return undefined;
    } catch (error) {
      console.error('Error fetching image:', error);
      return undefined;
    }
  };

  const formattedProductOptions = products?.map(product => ({
    value: product.productId,
    label: product.name
  }));

  const handleChange = (newValue: unknown) => {
    const selectedComponent = newValue as ProductOption;
    setCurrentSetId(selectedComponent.value);
    fetchProductSetComponents(selectedComponent.value);
  };

  const handleAddComponent = async () => {
    if (addComponentFormRef.current) {
      try {
        const isSuccess = await addComponentFormRef.current.submit();
        if (isSuccess) {
          await fetchProductSetComponents(currentSetId);
          addModalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleEditComponent = async () => {
    if (editComponentFormRef.current) {
      try {
        const isSuccess = await editComponentFormRef.current.submit();
        if (isSuccess) {
          await fetchProductSetComponents(currentSetId);
          editModalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDelete = async () => {
    if (currentProduct) {
      try {
        await axiosInstance.delete(
          `/api/v1/productSets/${currentProduct.productId}/components/${currentSetId}`
        );
        toast.success('Component deleted successfully', {
          ...toastConfig,
          autoClose: 2000
        });
        setCurrentProduct({
          productId: 0,
          quantity: 0,
          unitOfMeasure: 'Piece'
        });
        fetchProductSetComponents(currentSetId);
        deleteModalRef.current.hide();
      } catch (error) {
        toast.error('Failed to delete component. Please try again.', {
          ...toastConfig,
          autoClose: 3000
        });
        console.error(error);
      }
    }
  };

  useEffect(() => {
    if (debouncedInput) {
      fetchData(debouncedInput);
    } else {
      fetchData();
    }
  }, [debouncedInput]);

  return (
    <>
      <ReactSelect
        placeholder="Select product set"
        options={formattedProductOptions}
        onChange={handleChange}
        isLoading={isLoading}
        closeMenuOnSelect={true}
        isSearchable
        onInputChange={newValue => setInput(newValue)}
      />
      {productComponents && productComponents.length !== 0 && (
        <div className="mt-5">
          <Button
            onClick={() => addModalRef.current.show()}
            className="end-100 mb-4"
          >
            <FontAwesomeIcon icon={faPlusCircle} className="me-2" />
            Add component
          </Button>

          {productComponents.map(product => (
            <div
              key={product.productId}
              style={{
                width: '100%',
                marginBottom: '1rem',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #f2f2f2',
                borderRadius: '5px'
              }}
            >
              <div className="d-flex">
                <img
                  src={imageUrls[product.productId] || brandLogo}
                  alt={product.name || product.description}
                  className="me-3"
                  style={{
                    width: 50,
                    height: 50,
                    objectFit: 'cover',
                    borderRadius: '5px'
                  }}
                />
                <div>
                  <h5 className="mb-0">{product.name}</h5>
                  <small>{product.description}</small>
                </div>
              </div>

              <div>
                <FontAwesomeIcon
                  icon={faEdit}
                  style={{ cursor: 'pointer', marginRight: '1rem' }}
                  onClick={() => {
                    editModalRef.current.show();
                    setCurrentProduct({
                      productId: product.productId,
                      quantity: product.quantityPerSet,
                      unitOfMeasure: product.unitOfMeasure
                    });
                  }}
                />
                <FontAwesomeIcon
                  icon={faTrash}
                  style={{ cursor: 'pointer', color: '#C63C51' }}
                  onClick={() => {
                    setCurrentProduct({
                      productId: product.productId,
                      quantity: product.quantityPerSet
                    });
                    deleteModalRef.current.show();
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <CustomModal
        ref={addModalRef}
        title="Add Product Component"
        content={
          <AddProductComponentForm
            ref={addComponentFormRef}
            productId={currentSetId}
          />
        }
        onCancel={() => addModalRef.current.hide()}
        onSubmit={handleAddComponent}
      />

      <CustomModal
        ref={deleteModalRef}
        title="Delete product component"
        content={<>are you sure you want to delete this item</>}
        onCancel={() => deleteModalRef.current.hide()}
        onSubmit={handleDelete}
      />

      <CustomModal
        ref={editModalRef}
        title="Edit component"
        content={
          <EditProductComponent
            ref={editComponentFormRef}
            productId={currentSetId}
            componentId={currentProduct.productId}
            quantity={currentProduct.quantity}
            unitOfMeasure={currentProduct.unitOfMeasure}
          />
        }
        onCancel={() => editModalRef.current.hide()}
        onSubmit={handleEditComponent}
      />
      {/* <button onClick={() => console.log(productComponents)}>check</button> */}
    </>
  );
};

export default ProductSetList;
